/* eslint-disable react/no-unescaped-entities */
/* CONFILEX — Code de Conformité (document autonome).
 * Cf. mission "Code de Conformité Confilex" du 10/02/2026.
 * Ce document est indépendant des CGU/CGV/Confidentialité — il vient les compléter,
 * et non les remplacer.
 */
import React from "react";

const Chapter = ({ n, title, children, testId }) => (
  <section className="mb-10" data-testid={testId}>
    <h2 className="font-serif text-2xl text-[#0B3A2A] mb-4 leading-tight">
      <span className="text-[#C9A961] mr-2">{n}.</span> {title}
    </h2>
    <div className="text-slate-700 leading-relaxed space-y-4 text-[15px]">{children}</div>
  </section>
);

const Principle = ({ label, children }) => (
  <div className="rounded-lg border border-slate-200 bg-white p-4">
    <div className="font-medium text-[#0B3A2A] mb-1">{label}</div>
    <p className="text-slate-600 text-sm leading-relaxed">{children}</p>
  </div>
);

const Bullet = ({ items }) => (
  <ul className="list-disc pl-6 space-y-1.5 text-slate-700">
    {items.map((it, i) => <li key={i}>{it}</li>)}
  </ul>
);

export const CodeConformiteContent = () => (
  <article className="text-slate-800">
    {/* Header solennel */}
    <div className="mb-10 rounded-2xl border border-[#C9A961]/40 bg-gradient-to-br from-[#0B3A2A] to-[#1a5240] p-8 text-white">
      <div className="text-xs uppercase tracking-[0.25em] text-[#C9A961] mb-2">
        Référence officielle
      </div>
      <h1 className="font-serif text-3xl lg:text-4xl mb-3 leading-tight">
        Code de Conformité CONFILEX
      </h1>
      <p className="text-white/85 text-sm leading-relaxed max-w-3xl">
        Le présent Code constitue la référence officielle de CONFILEX en matière de conformité
        juridique, réglementaire, déontologique, technique et éthique. Il complète — sans les
        remplacer — les Conditions Générales d'Utilisation, les Conditions Générales de Vente,
        la Politique de confidentialité, la Politique de cookies et les Mentions légales.
      </p>
      <div className="mt-5 flex flex-wrap items-center gap-3 text-xs">
        <span className="inline-flex items-center gap-1 rounded-full bg-white/10 px-3 py-1">
          <span className="font-medium text-[#C9A961]">Version</span> 1.0
        </span>
        <span className="inline-flex items-center gap-1 rounded-full bg-white/10 px-3 py-1">
          <span className="font-medium text-[#C9A961]">Mise à jour</span> 10 février 2026
        </span>
        <span className="inline-flex items-center gap-1 rounded-full bg-white/10 px-3 py-1">
          <span className="font-medium text-[#C9A961]">Périmètre</span> Plateforme CONFILEX
        </span>
      </div>
    </div>

    {/* 1. Préambule */}
    <Chapter n="1" title="Préambule" testId="code-preambule">
      <p>
        CONFILEX est une plateforme technologique destinée à faciliter la préparation, la
        structuration documentaire, l'analyse assistée par intelligence artificielle, la
        négociation amiable et la mise en relation avec des professionnels réglementés,
        notamment les avocats, notaires, commissaires de justice, experts-comptables,
        administrateurs judiciaires et mandataires judiciaires.
      </p>
      <p className="rounded-lg border-l-4 border-[#C9A961] bg-[#C9A961]/5 py-3 px-4">
        <strong>CONFILEX n'exerce aucune profession réglementée</strong> au sens de la loi
        n° 71-1130 du 31 décembre 1971 et des textes qui régissent chacune des professions
        précitées. CONFILEX ne délivre <strong>aucune consultation juridique personnalisée</strong>,
        ne rédige aucun acte au nom d'un tiers, ne représente aucune partie et n'assume
        aucune des missions réservées à ces professionnels.
      </p>
      <p>
        Le présent Code de Conformité est destiné à démontrer que la conformité n'est pas,
        chez CONFILEX, une contrainte a posteriori, mais un principe structurel :
        <em> compliance by design </em> — la conformité est intégrée dès la conception des
        services et dans le fonctionnement quotidien de la plateforme.
      </p>
    </Chapter>

    {/* 2. Principes fondateurs */}
    <Chapter n="2" title="Principes fondateurs" testId="code-principes">
      <p>Onze principes structurent l'action et les décisions de CONFILEX :</p>
      <div className="grid md:grid-cols-2 gap-3 mt-3">
        <Principle label="Légalité">
          Le fonctionnement de CONFILEX s'inscrit strictement dans le cadre législatif et
          réglementaire français et européen applicable à la fourniture de services
          numériques et aux professions réglementées.
        </Principle>
        <Principle label="Indépendance des professionnels">
          CONFILEX ne s'immisce ni dans le contenu, ni dans les modalités, ni dans les
          honoraires des prestations délivrées par les professionnels référencés.
        </Principle>
        <Principle label="Neutralité">
          Aucun professionnel n'est présenté par CONFILEX comme intrinsèquement meilleur
          qu'un autre. Les résultats de recherche reposent sur des critères objectifs.
        </Principle>
        <Principle label="Transparence">
          Les critères de référencement, les visibilités payantes et les mécanismes de mise
          en relation sont documentés et accessibles en clair.
        </Principle>
        <Principle label="Loyauté">
          Toute communication commerciale, éditoriale ou algorithmique de CONFILEX est
          exempte de pratiques trompeuses au sens de l'article L121-1 du Code de la consommation.
        </Principle>
        <Principle label="Confidentialité">
          Les dossiers, documents et échanges déposés dans CONFILEX sont traités dans un
          environnement cloisonné et ne sont accessibles qu'aux personnes strictement habilitées.
        </Principle>
        <Principle label="Sécurité">
          CONFILEX met en œuvre des mesures techniques et organisationnelles appropriées
          à la nature des données et aux risques identifiés (art. 32 RGPD).
        </Principle>
        <Principle label="Protection des données">
          Les traitements de données à caractère personnel respectent le RGPD, la loi
          « Informatique et Libertés » et les recommandations de la CNIL.
        </Principle>
        <Principle label="Respect du contradictoire">
          Toute décision affectant un professionnel ou un utilisateur (suspension,
          déréférencement, refus) est motivée et peut faire l'objet d'un recours interne.
        </Principle>
        <Principle label="Responsabilité numérique">
          CONFILEX assume la responsabilité de la qualité, de la disponibilité et de la
          sécurité de son propre service, sans se substituer aux professionnels référencés.
        </Principle>
        <Principle label="Confiance des utilisateurs">
          L'intérêt légitime de l'utilisateur — préparer son dossier, choisir librement un
          professionnel, comprendre les limites de l'assistance apportée — prime sur toute
          logique commerciale de court terme.
        </Principle>
      </div>
    </Chapter>

    {/* 3. Respect des professions réglementées */}
    <Chapter n="3" title="Respect des professions réglementées" testId="code-professions-reglementees">
      <p>
        CONFILEX prend en compte les règles déontologiques et professionnelles applicables
        aux professions réglementées référencées, sans jamais s'y substituer. Sont
        notamment respectées :
      </p>
      <Bullet items={[
        "les règles du Conseil national des barreaux et de chaque barreau, notamment celles relatives au démarchage, à la publicité, aux honoraires, au secret professionnel et à l'indépendance de l'avocat ;",
        "les règles déontologiques applicables aux avocats (Règlement Intérieur National de la profession d'avocat) ;",
        "les règles applicables aux notaires (ordonnance du 2 novembre 1945 et son décret d'application) ;",
        "les règles applicables aux commissaires de justice (ordonnance n° 2016-728 du 2 juin 2016) ;",
        "les règles applicables aux experts-comptables (ordonnance n° 45-2138 du 19 septembre 1945 et Code de déontologie des professionnels de l'expertise comptable) ;",
        "les règles applicables aux commissaires aux comptes (Code de déontologie de la profession) ;",
        "les règles applicables aux administrateurs judiciaires et mandataires judiciaires (Code de commerce, livre VIII) ;",
        "les règles applicables aux autres professions réglementées susceptibles d'être référencées sur la plateforme.",
      ]} />
      <p className="rounded-lg border-l-4 border-[#0B3A2A] bg-[#0B3A2A]/5 py-3 px-4">
        En cas de contradiction entre une fonctionnalité de la plateforme et une règle
        déontologique applicable à un professionnel référencé, la règle déontologique
        prévaut et la fonctionnalité concernée est adaptée ou désactivée pour ce professionnel.
      </p>
    </Chapter>

    {/* 4. IA responsable */}
    <Chapter n="4" title="Intelligence artificielle responsable" testId="code-ia">
      <p>
        CONFILEX intègre des outils d'analyse assistée par intelligence artificielle. Leur
        usage obéit aux principes suivants, opposables à la plateforme comme à ses prestataires
        techniques :
      </p>
      <Bullet items={[
        "l'intelligence artificielle constitue exclusivement un outil d'assistance à l'utilisateur et au professionnel ;",
        "aucune décision juridique produisant des effets à l'égard d'une personne n'est prise par l'IA ;",
        "aucune consultation juridique personnalisée n'est délivrée par l'IA — elle est réservée aux professionnels réglementés ;",
        "l'utilisateur conserve à tout moment son libre arbitre : il peut ignorer, corriger, contester ou compléter les résultats produits ;",
        "le professionnel conserve son indépendance : il n'est pas tenu par les suggestions de l'IA et demeure seul responsable de ses conseils, actes et diligences ;",
        "les résultats de l'IA sont accompagnés de mentions courtes rappelant leur nature d'assistance et invitant à une vérification par un professionnel compétent ;",
        "les données transmises à un moteur d'IA sont minimisées, cloisonnées et, lorsque cela est techniquement possible, exclues de l'entraînement des modèles.",
      ]} />
      <p>
        CONFILEX suit avec attention l'entrée en vigueur du règlement européen sur
        l'intelligence artificielle (AI Act) et s'engage à adapter ses systèmes selon la
        catégorie de risque effective attribuée à ses cas d'usage.
      </p>
    </Chapter>

    {/* 5. Protection documentaire */}
    <Chapter n="5" title="Protection documentaire" testId="code-securite-doc">
      <p>
        Les dossiers et documents transmis à CONFILEX bénéficient d'une politique de sécurité
        alignée sur les exigences de l'article 32 du RGPD. Cette politique comprend notamment :
      </p>
      <Bullet items={[
        "chiffrement des données en transit (TLS 1.2 minimum) et chiffrement au repos des documents sensibles ;",
        "traçabilité des accès aux dossiers (journal d'audit horodaté, immuable dans son intégrité) ;",
        "journalisation des opérations administrateurs et des exports ;",
        "limitation des accès selon le principe du moindre privilège (RBAC — Role-Based Access Control) ;",
        "conservation limitée conforme à la matrice de durées documentée dans la Politique de confidentialité ;",
        "suppression automatique des documents à l'issue des durées applicables, sauf obligation légale de conservation ;",
        "protection contre les accès non autorisés (authentification forte, détection d'anomalies, limitation des requêtes) ;",
        "protection contre l'utilisation abusive des documents (interdiction de revente, de cession, d'utilisation publicitaire, d'entraînement de modèles sans consentement explicite).",
      ]} />
    </Chapter>

    {/* 6. Secret professionnel */}
    <Chapter n="6" title="Protection du secret professionnel" testId="code-secret-pro">
      <p>
        Le secret professionnel attaché aux échanges entre un professionnel réglementé et son
        client fait l'objet, chez CONFILEX, d'une attention particulière :
      </p>
      <Bullet items={[
        "les échanges entre un professionnel réglementé et son client transmis via la plateforme demeurent protégés au titre des textes propres à la profession du professionnel concerné (notamment l'article 66-5 de la loi n° 71-1130 du 31 décembre 1971 pour les avocats) ;",
        "CONFILEX met en œuvre les moyens techniques nécessaires à cette protection : cloisonnement, chiffrement, contrôle des accès administrateurs, absence d'utilisation secondaire du contenu ;",
        "la plateforme ne remet pas en cause les obligations déontologiques des professionnels et ne les incite à aucune pratique qui pourrait y porter atteinte ;",
        "CONFILEX ne bénéficie pas, en son nom propre, du secret professionnel des professions réglementées — celui-ci demeure attaché au professionnel lui-même.",
      ]} />
    </Chapter>

    {/* 7. Référencement impartial */}
    <Chapter n="7" title="Référencement impartial" testId="code-referencement">
      <p>Le référencement des professionnels sur CONFILEX obéit aux principes suivants :</p>
      <Bullet items={[
        "critères objectifs : matière juridique, spécialité officielle, domaine d'intervention déclaré, localisation, langue, disponibilité, acceptation de nouveaux dossiers ;",
        "transparence : les critères et leur pondération font l'objet d'une politique publique de classement, accessible depuis le pied de page ;",
        "absence de discrimination : aucun professionnel ne peut être défavorisé sur le fondement d'un critère prohibé par la loi ;",
        "possibilité de mise en avant dans le cadre d'un abonnement clairement identifié par la mention « Sponsorisé », sans qu'aucune incidence ne soit permise sur l'indépendance professionnelle du professionnel concerné ;",
        "absence de vente des dossiers : aucun professionnel ne peut « acheter » un dossier d'utilisateur — le libre choix de l'utilisateur reste souverain.",
      ]} />
      <p className="rounded-lg border-l-4 border-[#C9A961] bg-[#C9A961]/5 py-3 px-4">
        La visibilité payante n'est en aucun cas une appréciation de la compétence d'un
        professionnel. Elle n'est jamais assortie de mentions telles que « recommandé par
        CONFILEX » ou « meilleur avocat » et ne peut se substituer aux résultats naturels.
      </p>
    </Chapter>

    {/* 8. Intégrité économique */}
    <Chapter n="8" title="Intégrité économique" testId="code-integrite-eco">
      <p>Le modèle économique de CONFILEX obéit à quatre règles cardinales :</p>
      <Bullet items={[
        "CONFILEX ne vend pas les dossiers des utilisateurs — ni à un professionnel référencé, ni à un tiers, sous aucune forme et à aucun prix ;",
        "CONFILEX ne vend pas les données personnelles collectées via la plateforme — aucune cession commerciale de données n'est autorisée ;",
        "CONFILEX ne perçoit aucune rémunération proportionnelle aux honoraires des professionnels référencés — les abonnements sont forfaitaires et déconnectés du chiffre d'affaires que le professionnel réalise avec les utilisateurs ;",
        "le modèle économique de CONFILEX repose sur des abonnements et des services transparents, dont le prix, la nature et les modalités de résiliation sont documentés en clair avant toute souscription.",
      ]} />
    </Chapter>

    {/* 9. Gouvernance */}
    <Chapter n="9" title="Gouvernance de conformité" testId="code-gouvernance">
      <p>La conformité de CONFILEX est pilotée selon quatre engagements structurels :</p>
      <Bullet items={[
        "une revue régulière des textes juridiques applicables à la plateforme (CGU, CGV, Politique de confidentialité, Politique de classement, Politique de cookies, chartes, présente Code de Conformité) — au minimum une fois par an et lors de chaque évolution majeure ;",
        "une veille réglementaire permanente sur le droit français, le droit de l'Union européenne et la jurisprudence applicable, avec un accent particulier sur les évolutions du RGPD, du DSA, du DMA, de l'AI Act, des règles de consommation et des professions réglementées ;",
        "une mise à jour continue des documents contractuels et des mécanismes techniques, tracée dans un historique de versions ;",
        "un audit interne de conformité avant chaque évolution majeure de la plateforme, dont le résultat est consigné et conservé.",
      ]} />
    </Chapter>

    {/* 10. Comité interne */}
    <Chapter n="10" title="Comité interne de conformité" testId="code-comite">
      <p>
        CONFILEX se dote d'un <strong>Comité interne de conformité</strong>, qui peut associer
        des ressources internes et, si nécessaire, des expertises externes indépendantes. Ce
        Comité a pour missions :
      </p>
      <Bullet items={[
        "examiner les évolutions réglementaires françaises, européennes et sectorielles susceptibles d'impacter la plateforme ;",
        "évaluer les risques juridiques associés aux nouvelles fonctionnalités, aux nouveaux partenariats et aux nouveaux traitements de données ;",
        "contrôler la conformité des nouvelles fonctionnalités avant leur mise en production (revue « conformité by design ») ;",
        "proposer les mises à jour documentaires nécessaires (CGU, CGV, politiques annexes, présent Code) et suivre leur diffusion ;",
        "traiter les remontées et signalements internes ou externes relevant du présent Code.",
      ]} />
      <p>
        Le Comité rend compte de ses travaux à la direction de CONFILEX. Son fonctionnement
        interne fait l'objet d'une charte propre, séparée du présent document.
      </p>
    </Chapter>

    {/* 11. Signalement */}
    <Chapter n="11" title="Signalement des anomalies" testId="code-signalement">
      <p>
        CONFILEX met à disposition une procédure de signalement permettant à toute partie
        prenante de porter à sa connaissance une anomalie juridique, réglementaire, technique
        ou déontologique :
      </p>
      <Bullet items={[
        "aux utilisateurs — pour signaler une information incorrecte, un contenu inapproprié ou une fonctionnalité contestable ;",
        "aux professionnels référencés — pour signaler une usurpation de fiche, une donnée inexacte, un manquement au présent Code ou une pratique commerciale contestable ;",
        "aux autorités compétentes (CNIL, DGCCRF, ordres professionnels, juridictions) — dans le cadre de leurs missions respectives.",
      ]} />
      <p>
        Les signalements peuvent être transmis à l'adresse <strong>conformite@confilex.fr</strong>.
        Chaque signalement fait l'objet :
      </p>
      <Bullet items={[
        "d'un accusé de réception ;",
        "d'un numéro de dossier interne ;",
        "d'un délai indicatif de traitement communiqué au signaleur ;",
        "d'une réponse motivée à l'issue de l'instruction ;",
        "d'une possibilité d'escalade auprès du responsable conformité de CONFILEX en cas de désaccord.",
      ]} />
    </Chapter>

    {/* 12. Engagement */}
    <Chapter n="12" title="Engagement de CONFILEX" testId="code-engagement">
      <div className="rounded-2xl border border-[#0B3A2A]/20 bg-gradient-to-br from-white to-[#C9A961]/5 p-6">
        <p className="font-serif text-lg text-[#0B3A2A] italic leading-relaxed">
          « CONFILEX s'engage solennellement à développer, maintenir et faire évoluer ses
          services dans le respect :
        </p>
        <Bullet items={[
          "de la loi française applicable ;",
          "des droits fondamentaux, notamment ceux garantis par la Convention européenne des droits de l'homme et la Charte des droits fondamentaux de l'Union européenne ;",
          "des règles déontologiques propres à chacune des professions réglementées référencées ;",
          "du Règlement général sur la protection des données (RGPD) et de la loi française « Informatique et Libertés » ;",
          "du règlement européen sur les services numériques (DSA) et, lorsqu'il devient pleinement applicable, du règlement européen sur l'intelligence artificielle (AI Act) ;",
          "des principes de transparence, de sécurité, de loyauté et de confiance qui fondent la relation entre la plateforme, ses utilisateurs et les professionnels référencés. »",
        ]} />
      </div>
    </Chapter>

    {/* 13. Amélioration continue */}
    <Chapter n="13" title="Clause d'amélioration continue" testId="code-amelioration">
      <p>
        Le présent Code de Conformité constitue un <strong>document vivant</strong>. Il fera
        l'objet d'actualisations régulières afin d'intégrer :
      </p>
      <Bullet items={[
        "les évolutions législatives et réglementaires françaises et européennes ;",
        "les évolutions de la jurisprudence pertinente ;",
        "les évolutions des règles déontologiques applicables aux professions référencées ;",
        "les évolutions technologiques ayant un impact sur la conformité (IA, cryptographie, souveraineté des données) ;",
        "les retours des utilisateurs, des professionnels référencés, des autorités et de tout tiers intéressé.",
      ]} />
      <p>
        Chaque version publiée du présent Code porte un numéro et une date de mise à jour.
        L'historique des versions antérieures est conservé et peut être communiqué sur
        demande adressée à <strong>conformite@confilex.fr</strong>.
      </p>
    </Chapter>

    <div className="mt-16 border-t border-slate-200 pt-6 text-xs text-slate-500 italic">
      Le présent Code de Conformité complète, sans les remplacer, les Conditions Générales
      d'Utilisation, les Conditions Générales de Vente, la Politique de confidentialité, la
      Politique de cookies, les Mentions légales et l'ensemble des chartes et politiques
      annexes de CONFILEX. En cas de contradiction apparente, il est fait application du
      texte le plus protecteur pour l'utilisateur ou la personne concernée.
    </div>
  </article>
);

export default CodeConformiteContent;
